/**
 * jk-compact.js — карточка ЖК становится короче (дизайн 20.08.2026).
 *
 * Пара к public/css/jk-compact.css. Три задачи:
 *
 *   Т2  «Подобрать квартиру» → аккордеон, по умолчанию закрыт (телефон)
 *   Т3  описание «О ЖК …» → хвост под «Подробнее» (телефон)
 *   Т5  окно заявки (нижняя шторка) → галерея рендеров + форма в ОДНОМ окне
 *
 * Т4 («Способы покупки» плотнее) скриптом не трогается вовсе — там меняются
 * только размеры, а это дело таблицы стилей.
 *
 * ⛔ ПЛАНКИ СТАВИМ ВСЕГДА, А ПРЯЧЕМ СТИЛЯМИ. Т2 и Т3 — телефонная подача, но
 *    гасит их @media (min-width: 901px), а не проверка ширины в скрипте. Так
 *    поворот телефона в альбом (928-932 px у крупных моделей) обратим сам собой:
 *    правила перестают действовать, прод-вид возвращается целиком, и обратно
 *    тоже. Проверка ширины в скрипте дала бы одностороннюю дверь.
 *
 * ⛔ ШТОРКУ (Т5) ТАК НЕ СДЕЛАТЬ — там надо ДОБАВИТЬ узлы, поэтому ширину слушаем
 *    через matchMedia change: страницу могли открыть в альбоме, а потом
 *    повернуть в портрет, и без слушателя человек остался бы без галереи и
 *    формы навсегда. Обратный путь держат стили (см. «страховка на поворот» в
 *    jk-compact.css), поэтому разбирать построенное скриптом не нужно.
 *
 * ⛔ РАБОТАЕМ ПО DOMContentLoaded, А НЕ СРАЗУ. Файл подключён через $pageJS,
 *    то есть с defer, но кнопку шторки нам надо пересадить ПОСЛЕ того, как её
 *    подпишет js/jk.js. Порядок defer-скриптов это гарантирует, а вот зеркало
 *    (dev-mirror-proxy) вставляет сборку черновиков обычным <script> в конец
 *    тела — там она исполняется раньше всех штатных. Ожидание события уравнивает
 *    оба случая.
 */
(() => {
  'use strict';

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  const icon = (name) => {
    const i = document.createElement('i');
    i.className = 'fas ' + name;
    i.setAttribute('aria-hidden', 'true');
    return i;
  };

  /**
   * Раскрытие не должно уводить нажатую кнопку из-под пальца.
   *
   * ⛔ «Подробнее» стоит ПОД свёрнутыми абзацами: они появляются НАД кнопкой и
   *    сдвигают её вниз на всю свою высоту (замер на 405 px — 214 точек). Человек
   *    нажал и потерял место, куда смотрел. Подводим страницу на ту же разницу.
   * ⛔ Прокрутку двигаем 'auto', а не плавно: на плавной замер `top` сразу после
   *    вызова врёт, да и «догоняющая» страница при быстром двойном нажатии
   *    уезжает дважды.
   *
   * ⛔ У САМОГО НИЗА СТРАНИЦЫ ПОДВОДИТЬ НЕЧЕМ, И ЭТО НЕ ПОЛОМКА. Замер на 405 px:
   *    свернуть фильтр, стоя в самом низу документа, — высота прокрутки падает
   *    12 486 → 11 614, браузер сам прижимает страницу на те же 872 точки, и
   *    планка уезжает вниз экрана ровно на 872. Наш `scrollBy(+872)` при этом
   *    ничего не делает: запаса прокрутки больше нет. Вернуть планку на место
   *    физически невозможно — под ней исчезло содержимое, а тянуть некуда.
   *    Пальцем случай недостижим (надо нажать планку в 13 500 px над экраном),
   *    и «чинить» его подгонкой прокрутки значит дёргать страницу на ровном месте.
   */
  const keepInPlace = (node, toggle) => {
    const was = node.getBoundingClientRect().top;
    toggle();
    const delta = node.getBoundingClientRect().top - was;
    if (Math.abs(delta) > 1) window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Т2. Фильтр квартир — в аккордеон
  //
  // Планка встаёт ПЕРЕД шапкой фильтра. На живой странице это даёт порядок
  // «опросник „Сколько комнат нужно?“ (.fq) → планка → шапка»: опросник рисует
  // js/jk-filter-results.js уже после нас и кладёт его ПЕРВЫМ ребёнком панели
  // (`panel.insertBefore(quizApi.node, panel.firstChild)`), то есть ВЫШЕ планки.
  // ⛔ Порядок прочитан замером на живой странице (зеркало 8900, 1440 px,
  //    21.08: fq → zjk-foldbar → jk-filter__head → …), а не выведен рассуждением:
  //    прошлая редакция этого комментария рассуждала и ошиблась в обе стороны.
  // По решению владельца опросник остаётся на виду — под правило скрытия он не
  // подпадает. Всё, что ниже, — «Найдено N», сортировка, переключатели видов,
  // выдача и сводка — тоже не трогаем.
  // ─────────────────────────────────────────────────────────────────────────
  function foldFilter() {
    const panel = document.getElementById('jkFilter');
    if (!panel) return;
    if (panel.classList.contains('zjk-fold')) return;      // повторный запуск

    const head = panel.querySelector('.jk-filter__head');
    if (!head) return;

    const bar = el('button', 'zjk-foldbar');
    bar.type = 'button';
    bar.setAttribute('aria-controls', 'jkFilter');
    bar.setAttribute('aria-expanded', 'false');

    const t = el('span', 'zjk-foldbar__t');
    t.appendChild(icon('fa-sliders-h'));
    t.appendChild(el('span', null, 'Подобрать квартиру'));
    bar.appendChild(t);

    const chev = icon('fa-chevron-down');
    chev.classList.add('zjk-foldbar__x');
    bar.appendChild(chev);

    bar.addEventListener('click', () => {
      keepInPlace(bar, () => {
        const closed = panel.classList.toggle('is-closed');
        bar.setAttribute('aria-expanded', closed ? 'false' : 'true');
      });
    });

    panel.classList.add('zjk-fold', 'is-closed');
    panel.insertBefore(bar, head);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Т3. Описание ЖК — хвост под «Подробнее»
  //
  // Видимым остаётся первый абзац (.jk-about__lead) и полоса фактов; сворачиваем
  // хвостовые .jk-ai-desc-server__p.
  // ⛔ Абзац, внутри которого есть <strong>/<b>, помечаем .zjk-keep и оставляем
  //    на виду: владелец просил «видно только жирное». У большинства ЖК жирного
  //    в теле описания нет вовсе (единственные <b> — в плашках фактов «25 мин»),
  //    так что метка сработает не везде; она для тех ЖК, где текст другой.
  //
  // ⛔ ОПИСАНИЕ ОДНИМ КУСКОМ — ТОЖЕ ФОРМА ОПИСАНИЯ. Шаблон рисует .jk-about__lead
  //    только когда текст разбит на абзацы; если фид отдал его без пустых строк,
  //    в теле лежит ОДИН .jk-ai-desc-server__p и лида нет вовсе. Прежняя редакция
  //    прятала все .jk-ai-desc-server__p подряд — и от блока «О ЖК» оставалась
  //    полоса фактов с кнопкой, а весь текст пропадал (диверсия: тело 1145 → 184).
  //    Сегодня так не бывает — у всех 153 видимых домов ≥3 абзацев, — но это
  //    свойство данных, а не кода. Поэтому «лидом» назначаем первый абзац, если
  //    настоящего лида в разметке нет: под кнопку уходит только хвост.
  // ─────────────────────────────────────────────────────────────────────────
  function foldAbout() {
    const sec = document.getElementById('jkAiDescServer');
    if (!sec) return;
    if (sec.classList.contains('zjk-folddesc')) return;

    const body = sec.querySelector('.jk-ai-desc-server__body');
    if (!body) return;

    const tail = [...body.querySelectorAll('.jk-ai-desc-server__p')];
    if (tail.length === 0) return;                          // сворачивать нечего
    if (!body.querySelector('.jk-about__lead')) tail[0].classList.add('zjk-keep');

    let hidden = 0;
    tail.forEach((p) => {
      if (p.querySelector('strong, b')) p.classList.add('zjk-keep');
      if (!p.classList.contains('zjk-keep')) hidden++;
    });
    if (hidden === 0) return;

    const btn = el('button', 'zjk-more');
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    const label = el('span', null, 'Подробнее');
    btn.appendChild(label);
    btn.appendChild(icon('fa-chevron-down'));

    btn.addEventListener('click', () => {
      keepInPlace(btn, () => {
        const closed = sec.classList.toggle('is-closed');
        btn.setAttribute('aria-expanded', closed ? 'false' : 'true');
        label.textContent = closed ? 'Подробнее' : 'Свернуть';
      });
    });

    sec.classList.add('zjk-folddesc', 'is-closed');
    // ⛔ КНОПКА ЛОЖИТСЯ ЗА ТЕЛО ОПИСАНИЯ, А НЕ В НЕГО. Внутри тела она отбирала
    //    у последнего абзаца `:last-child`, а на нём висит прод-правило
    //    `.jk-ai-desc-server__p:last-child{margin-bottom:0}` (css/jk.css:2000) —
    //    и абзац возвращал себе 12 px нижнего поля. На компьютере кнопка скрыта
    //    (@media min-width:901px), но `display:none` структуру не отменяет:
    //    замер на 1440 px давал 733 → 745 px тела описания, три прогона из трёх.
    //    Медиа-обёрткой такое не лечится — правка разметки одна на оба экрана,
    //    поэтому узел уходит туда, где он ничьих отношений «первый/последний»
    //    не задевает: последним ребёнком .jk-container этой же секции. На
    //    телефоне место на экране то же самое — тело описания стоит в колонке
    //    последним, — а поле над кнопкой возвращает .zjk-more в таблице стилей.
    const host = sec.querySelector('.jk-container') || body;
    host.appendChild(btn);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Т5. Окно заявки: рендеры с листанием + форма прямо в окне
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Кадры для галереи.
   * ⛔ Своих фотографий у шторки нет и выдумывать пути нельзя. Основной источник —
   *    `data-shots` блока «Способы покупки»: сервер кладёт туда готовый список
   *    уже сжатых кадров (w=640), он есть в разметке с первой отрисовки и не
   *    зависит от того, догрузилась ли лента первого экрана.
   *    Запасной — галерея описания ЖК на этой же странице.
   */
  function shots() {
    const out = [];
    const push = (s) => { if (s && out.indexOf(s) === -1) out.push(s); };

    const bw = document.getElementById('jkBuyWays');
    if (bw && bw.dataset.shots) {
      try {
        const arr = JSON.parse(bw.dataset.shots);
        if (Array.isArray(arr)) arr.forEach((s) => { if (typeof s === 'string') push(s); });
      } catch (e) { /* список поехал — уходим на запасной источник */ }
    }
    if (out.length < 2) {
      document.querySelectorAll('.jk-about__gal img').forEach((img) => push(img.getAttribute('src')));
    }
    return out;
  }

  function buildGallery() {
    const list = shots();
    if (list.length === 0) return null;

    const box = el('div', 'zjk-gal bw-shots');
    const img = document.createElement('img');
    img.className = 'bw-shots__img';
    img.alt = 'Рендер жилого комплекса';
    img.decoding = 'async';
    img.loading = 'lazy';
    box.appendChild(img);

    let at = 0;
    let dots = null;

    const show = () => {
      img.setAttribute('src', list[at]);
      if (dots) [...dots.children].forEach((d, i) => d.classList.toggle('is-on', i === at));
    };
    const step = (d) => { at = (at + d + list.length) % list.length; show(); };

    if (list.length > 1) {
      const arrow = (dir, label, glyph) => {
        const b = el('button', 'bw-shots__nav bw-shots__nav--' + dir, glyph);
        b.type = 'button';
        b.setAttribute('aria-label', label);
        b.addEventListener('click', (e) => {
          // Клик по стрелке не должен всплывать: шторку закрывают жестами и
          // кликами по подложке, и чужой обработчик прочёл бы это как «закрыть».
          e.preventDefault();
          e.stopPropagation();
          step(dir === 'prev' ? -1 : 1);
        });
        return b;
      };
      box.appendChild(arrow('prev', 'Предыдущее фото', '‹'));
      box.appendChild(arrow('next', 'Следующее фото', '›'));

      dots = el('div', 'bw-shots__dots');
      list.forEach(() => dots.appendChild(el('span', 'bw-shots__dot')));
      box.appendChild(dots);

      // Свайп. ⛔ Гасим всплытие touchend: у самой шторки висит «свайп вниз =
      // закрыть», и без этого пролистывание кадров иногда захлопывало окно.
      let x0 = 0;
      let y0 = 0;
      box.addEventListener('touchstart', (e) => {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true });
      box.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          e.stopPropagation();
          step(dx < 0 ? 1 : -1);
        }
      });
    }

    show();
    return box;
  }

  const digits = (v) => {
    let d = String(v == null ? '' : v).replace(/\D/g, '');
    if (d[0] === '8' || d[0] === '7') d = d.slice(1);       // код страны, а не первая цифра
    return d.slice(0, 10);
  };

  function buildForm(bookBtn) {
    const form = document.createElement('form');
    form.className = 'zjk-sheetform';
    form.setAttribute('novalidate', 'novalidate');

    const phone = document.createElement('input');
    phone.type = 'tel';
    phone.className = 'zjk-input';
    phone.autocomplete = 'tel';
    phone.placeholder = '+7 (___) ___-__-__';
    phone.setAttribute('aria-label', 'Телефон');
    // Номер не должен попадать в Вебвизор — те же метки, что у штатных полей.
    phone.classList.add('ym-hide-content');
    phone.setAttribute('data-ym-disable-keys', 'true');
    form.appendChild(phone);

    const err = el('div', 'zjk-err', 'Введите телефон полностью');
    err.setAttribute('role', 'alert');
    form.appendChild(err);

    const agreeLbl = el('label', 'zjk-agree');
    const agree = document.createElement('input');
    agree.type = 'checkbox';
    const box = el('span', 'zjk-box', '✓');
    box.setAttribute('aria-hidden', 'true');
    agreeLbl.appendChild(agree);
    agreeLbl.appendChild(box);
    const agreeTxt = el('span');
    // ⛔ ТЕКСТ СОГЛАСИЯ — СЛОВО В СЛОВО КАК У ОСТАЛЬНЫХ ФОРМ САЙТА.
    //    В pdn_consents с каждой формы уходит ОДИН И ТОТ ЖЕ PDN_TEXT_VERSION,
    //    то есть номер версии обещает РКН, что человек видел один и тот же текст.
    //    Четвёртая формулировка рядом с тремя одинаковыми (app/templates/footer.php,
    //    js/jk-card.js, js/jk-buy-ways.js) делала этот номер неправдой, а хук
    //    tools/check-pdn-version-bump.sh стережёт только /policy.php и такое
    //    расхождение не видит. Сторож — тест «текст согласия совпадает с общим».
    agreeTxt.appendChild(document.createTextNode('Согласен на обработку моих персональных данных (имя, телефон). '));
    const link = document.createElement('a');
    link.href = '/soglasie.php';
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Согласие на обработку ПДн';
    agreeTxt.appendChild(link);
    agreeLbl.appendChild(agreeTxt);
    form.appendChild(agreeLbl);

    form.appendChild(bookBtn);

    // ⛔ СВАЙП ВНИЗ НАД ФОРМОЙ НЕ ЗАКРЫВАЕТ ШТОРКУ. У #jkBottomSheet висит
    //    штатное «протянул вниз больше 80 px — закрыть» (js/jk.js:4455-4465), и
    //    откуда пришёл жест, оно не спрашивает. Пока в шторке были только
    //    заголовок, цена и кнопка, терять было нечего; теперь там набранный
    //    номер, и протяжка над полем ввода стирала его вместе с окном.
    //    Гасим всплытие touchend, начавшегося ВНУТРИ формы. Жест по «ручке»,
    //    заголовку, цене и обещаниям при этом закрывает окно как раньше:
    //    касание целится в тот узел, где оно НАЧАЛОСЬ, и до шторки доходит.
    //    Лечим здесь, а не в jk.js: форма — наша, и без неё чинить нечего.
    form.addEventListener('touchend', (e) => { e.stopPropagation(); });

    phone.addEventListener('input', () => {
      const d = digits(phone.value);
      if (!d.length) { phone.value = ''; return; }
      let out = '+7 (' + d.slice(0, 3);
      if (d.length >= 4) out += ') ' + d.slice(3, 6);
      if (d.length >= 7) out += '-' + d.slice(6, 8);
      if (d.length >= 9) out += '-' + d.slice(8, 10);
      phone.value = out;
      if (d.length === 10) { phone.classList.remove('is-bad'); err.classList.remove('is-on'); }
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const d = digits(phone.value);
      if (d.length !== 10) {
        phone.classList.add('is-bad');
        err.textContent = 'Введите телефон полностью';
        err.classList.add('is-on');
        phone.focus();
        return;
      }
      // ⛔ 152-ФЗ ст. 9 ч. 4: без ОТМЕЧЕННОГО согласия заявку не отправляем вовсе,
      //    и шлём РЕАЛЬНОЕ состояние флажка, а не единицу константой — строка
      //    уходит в pdn_consents, журнал, который показывают РКН.
      if (!agree.checked) {
        if (typeof window.showConsentHint === 'function') window.showConsentHint(agree);
        err.textContent = 'Отметьте согласие на обработку персональных данных';
        err.classList.add('is-on');
        return;
      }

      bookBtn.disabled = true;
      // ⛔ Содержимое кнопки прячем УЗЛАМИ, а не через innerHTML: значок внутри
      //    кнопки — чужая разметка, и гонять её строкой туда-обратно значит
      //    держать в коде путь «строка → HTML» там, где он не нужен вовсе.
      const was = [...bookBtn.childNodes];
      bookBtn.textContent = 'Отправляем…';

      const body = new URLSearchParams();
      const native = document.getElementById('callbackForm');
      if (native) {
        native.querySelectorAll('input[type=hidden]').forEach((h) => { if (h.name) body.append(h.name, h.value); });
      } else if (window._csrfToken) {
        body.append('_csrf', window._csrfToken);
      }
      body.append('_hp', '');
      body.append('name', 'Заявка с карточки ЖК');
      body.append('phone', '+7' + d);
      body.append('consent_main', agree.checked ? '1' : '');
      // ⛔ Метка «откуда пришла заявка»: без неё SendHandler ставит всем «Сайт».
      // Страница комнатности называет себя сама (window.BRONI_LEAD_PAGE, flats.php) —
      // владелец видит, что заявка не с карточки ЖК (проверка 25.09.2026, P2-4).
      body.append('_source', (typeof window.BRONI_LEAD_PAGE === 'string' && window.BRONI_LEAD_PAGE ? window.BRONI_LEAD_PAGE : 'Карточка ЖК') + ' — нижняя шторка');
      body.append('comment', 'Бронирование: ' + ((window.JK && window.JK.name) || document.title));

      fetch('/send.php', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      })
        // ⛔ Без проверки r.ok «Заявка принята» рисовалась бы и на 403 при
        //    непрошедшем CSRF: человек уходит довольным, а заявки нет.
        .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r; })
        .then(() => {
          form.textContent = '';
          const ok = el('div', 'zjk-done');
          ok.appendChild(el('b', null, 'Заявка принята'));
          ok.appendChild(el('span', null, 'Перезвоним в течение 15 минут в рабочее время. Номер никому не передаём.'));
          form.appendChild(ok);
          // ⛔ ЦЕЛЬ УХОДИТ В ВЕТКЕ УСПЕШНОГО ОТВЕТА, а не по клику: цель на клике
          //    считает намерения, а владелец по этой цифре считает деньги.
          //    Имя берём СУЩЕСТВУЮЩЕЕ — jk_callback_modal_sent: его уже шлёт
          //    js/jk-card.js (то же «заявка с карточки ЖК», только с компьютера),
          //    значит цель настроена в счётчике. Новое имя пришлось бы заводить
          //    руками в Метрике, и до этого оно не считалось бы нигде.
          //    Сторож — tests/JS/lead-form-ym-goal.test.js (реестр форм).
          if (typeof window.ymGoal === 'function') window.ymGoal('jk_callback_modal_sent', { source: 'jk_sheet' });
        })
        .catch(() => {
          bookBtn.disabled = false;
          bookBtn.textContent = '';
          was.forEach((n) => bookBtn.appendChild(n));
          err.textContent = 'Не получилось отправить. Попробуйте ещё раз или позвоните нам.';
          err.classList.add('is-on');
        });
    });

    return form;
  }

  /**
   * Шторка «Забронировать»: рендеры + форма прямо здесь.
   *
   * ⛔ Кнопку «Оставить заявку» ПЕРЕСАЖИВАЕМ КОПИЕЙ, а не вешаем свой обработчик
   *    поверх. Штатный код (jk.js) уже подписал оригинал на «закрыть шторку и
   *    открыть вторую форму» — ровно то, от чего владелец и просил уйти. Снять
   *    чужую подписку нечем: ссылки на функцию у нас нет. Копия узла подписок не
   *    наследует.
   * ⛔ ОРИГИНАЛ ОСТАЁТСЯ В РАЗМЕТКЕ, ПРОСТО СПРЯТАН (.zjk-off). Удалять его
   *    нельзя по трём причинам: на его id висит делегат jk-card.js
   *    (CARD_LEAD_SOURCES → «нижняя шторка»), tests/JS/jk-card-lead-source
   *    требует, чтобы каждый id из того списка рендерился живой разметкой, и,
   *    главное, при повороте телефона в альбом класс перестаёт действовать —
   *    кнопка возвращается на место сама, без единой строки скрипта.
   * ⛔ У КОПИИ СНИМАЕМ id. Иначе на странице оказались бы два узла с одним
   *    jkSheetBookBtn, и getElementById отдавал бы то один, то другой.
   */
  function sheet() {
    const box = document.getElementById('jkBottomSheet');
    if (!box) return;
    if (box.querySelector('.zjk-sheetform')) return;        // повторный запуск

    const orig = document.getElementById('jkSheetBookBtn');
    if (!orig || !orig.parentNode) return;

    const sub = box.querySelector('.jk-bottom-sheet__sub');
    const gal = buildGallery();
    if (gal && sub) sub.parentNode.insertBefore(gal, sub.nextSibling);

    const btn = orig.cloneNode(true);
    btn.removeAttribute('id');
    btn.type = 'submit';
    orig.classList.add('zjk-off');

    box.appendChild(buildForm(btn));
  }

  const start = () => {
    foldFilter();
    foldAbout();

    // ⛔ Ширину для шторки СЛУШАЕМ, а не спрашиваем один раз. Страницу могли
    //    открыть в альбоме (928-932 px у крупных телефонов — это ШИРЕ 900) и
    //    повернуть в портрет: без слушателя человек остался бы без галереи и
    //    формы до перезагрузки. Обратный путь держат стили, разбирать нечего.
    const mq = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    if (!mq) { sheet(); return; }
    if (mq.matches) sheet();
    const onChange = (e) => { if (e.matches) sheet(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
